import { ChangeDetectionStrategy, Component, inject, input, output, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { ZagIcon } from '../icon/icon';

/**
 * Data structure for RoleListRow component
 */
export interface RoleListRowData {
  id: string;
  description?: string | null;
  permissionCount: number;
  userCount: number;
  groupCount: number;
}

/**
 * RoleListRow Component
 *
 * Displays a role's information in a horizontal row format for list views.
 * Shows role ID, description, and stats (permissions, users, groups).
 *
 * @example
 * ```html
 * <zag-role-list-row
 *   [role]="roleData"
 *   [editRoute]="['/settings/roles', roleData.id]"
 *   (rowClick)="onRoleSelected($event)"
 *   (deleteClick)="onDeleteRole($event)">
 * </zag-role-list-row>
 * ```
 */
@Component({
  selector: 'zag-role-list-row',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [ZagIcon],
  host: {
    '[class.zag-role-list-row]': 'true',
    '[attr.role]': '"button"',
    '[attr.tabindex]': '0',
    '(click)': 'onRowClick()',
    '(keydown)': 'onRowKeydown($event)',
  },
  template: `
    <span zagIcon="security" class="zag-role-list-row__icon"></span>

    <div class="zag-role-list-row__info">
      <span class="zag-role-list-row__name">{{ role().id }}</span>
      @if (role().description) {
        <span class="zag-role-list-row__description">{{ role().description }}</span>
      }
    </div>

    <div class="zag-role-list-row__stats">
      <span class="zag-role-list-row__stat">
        <span zagIcon="key"></span>
        {{ role().permissionCount }}
      </span>
      <span class="zag-role-list-row__stat">
        <span zagIcon="person"></span>
        {{ role().userCount }}
      </span>
      <span class="zag-role-list-row__stat">
        <span zagIcon="groups"></span>
        {{ role().groupCount }}
      </span>
    </div>

    <div class="zag-role-list-row__actions">
      @if (showDelete()) {
        <button
          type="button"
          class="zag-role-list-row__action zag-role-list-row__action--delete"
          [disabled]="deleteDisabled()"
          [attr.aria-label]="deleteAriaLabel() || null"
          (click)="onDeleteClick($event)"
        >
          <span zagIcon="delete"></span>
        </button>
      }
    </div>
  `,
  styles: [
    `
      .zag-role-list-row {
        display: flex;
        align-items: center;
        padding: var(--spacing-sm) var(--spacing-md);
        gap: var(--spacing-md);
        cursor: pointer;
        transition: box-shadow 0.15s ease;
        background-color: var(--surface-color-default);
        border: 1px solid var(--border-color-default);
        border-radius: var(--border-radius-md);

        &:focus-visible {
          outline: 2px solid var(--color-primary);
          outline-offset: 2px;
        }
      }

      .zag-role-list-row__icon {
        font-size: 24px;
        color: var(--color-primary);
        flex-shrink: 0;
      }

      .zag-role-list-row__info {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 2px;
        min-width: 0;
      }

      .zag-role-list-row__name {
        font-weight: 500;
        color: var(--text-color-primary);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .zag-role-list-row__description {
        font-size: var(--font-size-sm);
        color: var(--text-color-secondary);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .zag-role-list-row__stats {
        display: flex;
        align-items: center;
        gap: var(--spacing-md);
        flex-shrink: 0;
      }

      .zag-role-list-row__stat {
        display: flex;
        align-items: center;
        gap: var(--spacing-xs);
        font-size: var(--font-size-sm);
        color: var(--text-color-tertiary);

        [zagIcon] {
          font-size: 16px;
        }
      }

      .zag-role-list-row__actions {
        display: flex;
        align-items: center;
        gap: var(--spacing-xs);
        flex-shrink: 0;
      }

      .zag-role-list-row__action {
        flex-shrink: 0;
        padding: var(--spacing-xs);
        color: var(--text-color-secondary);
        border: none;
        background: none;
        border-radius: var(--border-radius-sm);
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        text-decoration: none;
        transition:
          background-color 0.15s,
          color 0.15s;

        &:hover {
          background-color: var(--color-info-light);
          color: var(--color-primary);
        }

        &:disabled {
          opacity: 0.5;
          cursor: not-allowed;

          &:hover {
            background-color: transparent;
            color: var(--text-color-secondary);
          }
        }

        [zagIcon] {
          font-size: 18px;
        }
      }

      .zag-role-list-row__action--delete {
        &:hover:not(:disabled) {
          background-color: var(--color-error-light);
          color: var(--color-error);
        }
      }

      @media (max-width: 768px) {
        .zag-role-list-row__stats {
          display: none;
        }
      }
    `,
  ],
})
export class RoleListRow {
  private readonly router = inject(Router);

  /** The role data to display */
  readonly role = input.required<RoleListRowData>();

  /** Route for the edit link (array format for routerLink) */
  readonly editRoute = input<string[]>();

  /** Accessible label for the edit action */
  readonly editAriaLabel = input('');

  /** Whether to show the delete button */
  readonly showDelete = input(true);

  /** Accessible label for the delete action */
  readonly deleteAriaLabel = input('');

  /** Whether the delete button is disabled */
  readonly deleteDisabled = input(false);

  /** Emitted when the row is clicked */
  readonly rowClick = output<RoleListRowData>();

  /** Emitted when delete button is clicked */
  readonly deleteClick = output<RoleListRowData>();

  /** Handle row click - navigates to editRoute if set, then emits rowClick */
  onRowClick(): void {
    const route = this.editRoute();
    if (route) {
      this.router.navigate(route);
    }
    this.rowClick.emit(this.role());
  }

  /** Handle keyboard activation for accessibility */
  onRowKeydown(event: KeyboardEvent): void {
    const target = event.target as HTMLElement | null;
    if (target?.closest('a') || target?.closest('button')) {
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.onRowClick();
    }
  }

  /** Handle delete button click */
  onDeleteClick(event: Event): void {
    event.stopPropagation();
    this.deleteClick.emit(this.role());
  }
}
